import { useMemo } from 'react';
import { KWELLO_ICON, KwelloSvgIcon } from '../icon/KwelloSvgIcon';
import ContactSupportButton from './ContactSupportButton';
import ReloadButton from './ReloadButton';
import HomeButton from './HomeButton';
import './errorpage.scss';
import { useLocation } from 'react-router-dom';
import { Typography } from '@progress/kendo-react-common';

export enum ErrorType {
  ERROR = 'error',
  NOT_FOUND = 'not-found',
  PERMISSION = 'permission',
  MOBILE_DEVICE = 'mobile-device',
  WIDGET_ERROR = 'widget-error',
}

interface ErrorPageProps {
  errorType?: ErrorType;
  titleOverride?: string;
  messageOverride?: string;
  widgetType?: string;
}

function ErrorPage({
  errorType,
  titleOverride,
  messageOverride,
  widgetType,
}: Readonly<ErrorPageProps>) {
  const { state } = useLocation();
  const { title, message } = state?.error ?? { title: titleOverride, message: messageOverride };

  const errorValues = useMemo(() => {
    if (errorType === ErrorType.NOT_FOUND) {
      return {
        icon: KWELLO_ICON.SEARCH_FOR_ICON,
        title: "Oops! The page you're looking for doesn't exist.",
        message: "It seems you've hit a broken link or the page has moved.",
        buttons: [<ContactSupportButton buttonColor="secondary" key={errorType} />],
      };
    }
    if (errorType === ErrorType.PERMISSION) {
      return {
        icon: KWELLO_ICON.PERMISSION_PADLOCK,
        title: 'You don’t have permission to access this page.',
        message: '',
        buttons: [
          <HomeButton key={`${errorType}-home`} />,
          <ContactSupportButton className="blue-200-button" key={`${errorType}-support`} />,
        ],
      };
    }
    if (errorType === ErrorType.MOBILE_DEVICE) {
      return {
        icon: KWELLO_ICON.MOBILE_DEVICE,
        title: "It looks like you're on a mobile device",
        message:
          'Kwello is best experienced on a tablet or desktop. Please switch for full functionality.',
        buttons: [<ContactSupportButton buttonColor="secondary" key={errorType} />],
      };
    }
    //default is type of 'error'
    return {
      icon: KWELLO_ICON.BALLOON_DEFLATE,
      title: 'Oops! Something went wrong.',
      message: 'Please try again or contact support if the issue persists.',
      buttons: [
        <ReloadButton key={`${errorType}-reload`} />,
        <ContactSupportButton
          buttonColor="secondary"
          className="blue-200-button"
          key={`${errorType}-support`}
        />,
      ],
    };
  }, [errorType]);
  // Special case for widget error: because of the design it is here
  if (errorType === ErrorType.WIDGET_ERROR) {
    const handleContactSupport = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      if (typeof window !== 'undefined' && (window as any).Intercom) {
        (window as any).Intercom('showNewMessage');
      }
    };
    return (
      <div className={`widget-error-state ${widgetType === 'counter' ? 'vertical' : ''}`}>
        <KwelloSvgIcon icon={KWELLO_ICON.DATA_LOAD_ERROR_ICON} />
        <div className="text-content">
          <Typography.h6>Unable to Load Data</Typography.h6>
          <Typography.p>
            Try refreshing or{' '}
            <a href="#" onClick={handleContactSupport}>
              contact support
            </a>
            .
          </Typography.p>
        </div>
      </div>
    );
  }

  return (
    <div className="error-page-container">
      <div className="left-side">
        <KwelloSvgIcon icon={errorValues.icon} />
      </div>
      <div className="right-side">
        <Typography.h4 className="message-title">{title ?? errorValues.title}</Typography.h4>
        <Typography.p className="message-content">{message ?? errorValues.message}</Typography.p>
        <div className="buttons">
          {errorValues.buttons?.map((button, index) => (
            <span key={`${errorType}-${index}`}>{button}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
export default ErrorPage;
